import { Injectable } from '@angular/core';
import {
    Messaging,
    getToken,
    onMessage,
    MessagePayload
} from 'firebase/messaging';
import { FirebaseService } from './firebase.service';
import { Observable } from 'rxjs';

/**
 * Firebase Cloud Messaging Service
 * Handles push notifications
 */
@Injectable({
    providedIn: 'root'
})
export class FirebaseMessagingService {
    private messaging: Messaging | null;

    // VAPID key for FCM (you'll need to generate this from Firebase Console)
    // Navigate to: Project Settings > Cloud Messaging > Web Push certificates
    private readonly VAPID_KEY = 'YOUR_VAPID_KEY_HERE';

    constructor(private firebaseService: FirebaseService) {
        this.messaging = this.firebaseService.messaging;
    }

    /**
     * Request notification permission and get FCM token
     */
    async requestPermission(): Promise<string | null> {
        if (!this.messaging) {
            console.warn('Firebase Messaging is not available');
            return null;
        }

        try {
            const permission = await Notification.requestPermission();

            if (permission === 'granted') {
                console.log('Notification permission granted');
                return await this.getToken();
            } else {
                console.log('Notification permission denied');
                return null;
            }
        } catch (error) {
            console.error('Error requesting permission:', error);
            throw error;
        }
    }

    /**
     * Get FCM registration token
     */
    async getToken(): Promise<string | null> {
        if (!this.messaging) {
            console.warn('Firebase Messaging is not available');
            return null;
        }

        try {
            const token = await getToken(this.messaging, {
                vapidKey: this.VAPID_KEY
            });

            if (token) {
                console.log('FCM Token:', token);
                return token;
            } else {
                console.log('No registration token available');
                return null;
            }
        } catch (error) {
            console.error('Error getting token:', error);
            throw error;
        }
    }

    /**
     * Listen to foreground messages
     */
    listenToMessages(): Observable<MessagePayload> {
        return new Observable(subscriber => {
            if (!this.messaging) {
                console.warn('Firebase Messaging is not available');
                subscriber.complete();
                return;
            }

            const unsubscribe = onMessage(this.messaging, (payload: MessagePayload) => {
                console.log('Message received:', payload);
                subscriber.next(payload);
            });

            return () => {
                // Cleanup if needed
            };
        });
    }

    /**
     * Display notification
     */
    showNotification(title: string, options?: NotificationOptions): void {
        if ('Notification' in window && Notification.permission === 'granted') {
            new Notification(title, options);
        }
    }

    /**
     * Check if notifications are supported
     */
    isNotificationSupported(): boolean {
        return 'Notification' in window && !!this.messaging;
    }

    /**
     * Get current notification permission status
     */
    getPermissionStatus(): NotificationPermission {
        if ('Notification' in window) {
            return Notification.permission;
        }
        return 'denied';
    }
}
